import { useEffect, useState } from "react";
import { Link2, Link2Off, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import QboFailures from "@/components/QboFailures";
import QboHistory from "@/components/QboHistory";

export default function QuickBooksCard() {
  const [st, setSt] = useState(null);
  const [auto, setAuto] = useState(false);
  const [nightly, setNightly] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/qbo/status"), api.get("/qbo/settings")])
    .then(([a, b]) => { setSt(a.data); setAuto(b.data.auto_send); setNightly(b.data.last_nightly); }).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const checkNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/qbo/run-nightly");
      setNightly(data);
      data.error ? toast.error(data.error) : toast.success(`Checked ${data.checked} invoices · ${data.newly_paid} newly paid`);
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const connect = async () => {
    setBusy(true);
    try { window.location.href = (await api.get("/qbo/connect")).data.url; } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/qbo/disconnect"); toast.success("QuickBooks disconnected"); load(); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const toggleAuto = async (v) => {
    try {
      await api.put("/qbo/settings", { auto_send: v });
      setAuto(v);
      toast.success(v ? "New and paid invoices will go to QuickBooks automatically" : "Invoices go to QuickBooks only when you press Send");
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!st) return <div className="mt-6 border rounded-lg bg-card p-5"><Loader2 className="animate-spin text-primary" /></div>;
  return (
    <div className="mt-6 border rounded-lg bg-card p-5" data-testid="qbo-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="h-10 w-10 rounded-md bg-emerald-600 flex items-center justify-center font-bold text-white">qb</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold">QuickBooks Online</p>
          <p className="text-xs text-muted-foreground" data-testid="qbo-status">
            {!st.configured ? "Waiting for your QuickBooks app keys" : `${st.connected ? `Connected · company ${st.realm_id}` : "Not connected"} · ${st.environment === "production" ? "Real books" : "Test company (sandbox)"}`}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${st.environment === "production" ? "bg-emerald-600/20 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="qbo-environment-badge">
          {st.environment === "production" ? "LIVE" : "TEST"}
        </span>
        {st.connected
          ? <Button variant="outline" onClick={disconnect} disabled={busy} data-testid="qbo-disconnect-button" className="text-red-400 hover:bg-red-500/10 hover:text-red-400"><Link2Off size={16} /> Disconnect</Button>
          : <Button onClick={connect} disabled={busy || !st.configured} data-testid="qbo-connect-button" className="bg-emerald-600 hover:bg-emerald-700 text-white"><Link2 size={16} /> Connect QuickBooks</Button>}
      </div>
      <label className="mt-4 flex items-start gap-3 border-t pt-4 cursor-pointer">
        <Switch checked={auto} onCheckedChange={toggleAuto} data-testid="qbo-auto-switch" />
        <span>
          <span className="text-sm font-medium block">Send automatically</span>
          <span className="text-xs text-muted-foreground">Off: invoices go to QuickBooks only when you press "Send to QuickBooks". On: new, edited and paid invoices (not drafts) are sent right away.</span>
        </span>
      </label>
      <div className="mt-4 flex flex-wrap items-center gap-3 border-t pt-4 text-sm">
        <span className="font-medium">Nightly payment check</span>
        <span className="text-xs text-muted-foreground" data-testid="qbo-nightly-status">
          Runs every night at 2:00 AM Central. {nightly ? `Last run ${new Date(nightly.at).toLocaleString()}: ${nightly.error ? nightly.error : `${nightly.checked} checked, ${nightly.newly_paid} newly paid`}` : "Hasn't run yet."}
        </span>
        <Button size="sm" variant="outline" onClick={checkNow} disabled={busy || !st.connected} className="ml-auto" data-testid="qbo-check-now-button">Check now</Button>
      </div>
      <QboFailures connected={st.connected} />
      <QboHistory />
      {st.environment !== "production" && (
        <p className="mt-4 border-t pt-4 text-xs text-muted-foreground" data-testid="qbo-go-live-help">
          <span className="font-medium text-foreground">Going live:</span> once Intuit approves your production keys, ask me to switch to your real books. Invoices sent to the test company will then be sent fresh to your real books.
        </p>
      )}
    </div>
  );
}
